// Refine stores lightweight editorial review metadata on the Assembly.
// The actual clip in/out points remain the only source of truth for NLE exports.
const EPS=0.00001;
export const REFINE_FALLBACK_STEP=.01;
export function sourceFrameStep(source){
 const num=Number(source?.fpsNum),den=Number(source?.fpsDen);
 const certified=['confirmed-cfr','detected-cfr','demo-cfr'].includes(source?.frameStatus);
 return certified&&num>0&&den>0&&num/den<=120 ? den/num:null;
}
export function sourceBounds(block,source){
 return {min:0,max:Math.max(Number(source?.duration)||0,block.end)};
}
export function snapSourceTime(value,source){
 const step=sourceFrameStep(source);
 return step ? Math.round(value/step)*step : Math.round(value*100)/100;
}
export function refineTrim(paper,id,edge,value){
 if(!paper?.blocks||!['start','end'].includes(edge)||!Number.isFinite(value))
  throw new Error('Enter a valid clip boundary.');
 const block=paper.blocks.find(part=>part.id===id);
 if(!block)throw new Error('This passage no longer exists in Assembly.');
 const source=paper.sources?.[block.sourceId];
 if(!source)throw new Error('This passage is missing its source interview.');
 const minGap=sourceFrameStep(source)||REFINE_FALLBACK_STEP;
 const {max}=sourceBounds(block,source);
 const snapped=Math.min(max,Math.max(0,snapSourceTime(value,source)));
 const next={...block,[edge]:snapped};
 if(next.end-next.start<minGap-EPS)
  throw new Error('Keep at least one source frame between the in and out points.');
 if(Math.abs(next[edge]-block[edge])<EPS)return paper;
 // Do NOT rebuild block.text when handles move; the editor's chosen wording
 // and all source word IDs are preserved.
 return {...paper,blocks:paper.blocks.map(part=>part.id===id?next:part)};
}
// One safe editorial action: change a source handle and establish a
// reversible baseline only if the nudge actually changes the edit.
export function nudgeRefined(paper,id,edge,frames){
 if(!Number.isInteger(frames)||Math.abs(frames)>25)throw new Error('Choose a valid frame adjustment.');
 const block=paper?.blocks?.find(part=>part.id===id);
 if(!block)throw new Error('This passage is no longer in Assembly.');
 const source=paper.sources?.[block.sourceId];
 const step=sourceFrameStep(source)||REFINE_FALLBACK_STEP;
 const next=refineTrim(paper,id,edge,block[edge]+frames*step);
 if(next===paper)return paper;
 const prepared=prepareRefine(paper);
 return {...next,refine:prepared.refine};
}
export function refineHandleWindow(paper,block,edge,source,shownValue=block?.[edge]){
 if(!['start','end'].includes(edge)||!block||!Number.isFinite(shownValue))
  throw new Error('Choose a valid source handle.');
 const step=sourceFrameStep(source)||REFINE_FALLBACK_STEP;
 const sourceEnd=sourceBounds(block,source).max;
 const baseline=paper?.refine?.lockedHandles?.[block.id]?.[edge];
 const focus=Number.isFinite(baseline)?baseline:block[edge];
 const minAllowed=edge==='start'?0:Math.min(sourceEnd,block.start+step);
 const maxAllowed=edge==='start'?Math.max(0,block.end-step):sourceEnd;
 const min=Math.max(minAllowed,Math.floor((Math.min(shownValue,focus)-1.5)/step)*step);
 const max=Math.max(min,Math.min(maxAllowed,Math.ceil((Math.max(shownValue,focus)+1.5)/step)*step));
 return {min,max,step};
}
export function blockReviewSignature(block){
 return JSON.stringify([block.id,block.sourceId,block.text,Math.round(block.start*100000),Math.round(block.end*100000)]);
}
export function storyLockSignature(paper){
 return JSON.stringify((paper?.blocks||[]).map(b=>[b.id,b.sourceId,b.text]));
}
export function isStoryLocked(paper){
 return Boolean(paper?.refine?.lockedAt&&paper.refine?.structure===storyLockSignature(paper));
}
export function lockStory(paper,when=new Date().toISOString()){
 if(!paper?.blocks?.length)throw new Error('Add passages to Assembly before locking the story.');
 const lockedHandles=Object.fromEntries(paper.blocks.map(block=>[block.id,{
  start:block.start,end:block.end
 }]));
 return {...paper,refine:{...paper.refine,lockedAt:when,
  structure:storyLockSignature(paper),lockedHandles}};
}
// The first real trim or review creates a reversible baseline automatically.
// Refine never needs a blocking manual "Lock story" step.
export function prepareRefine(paper){
 return isStoryLocked(paper)?paper:lockStory(paper);
}
export function unlockStory(paper){
 return {...paper,refine:{...paper.refine,lockedAt:null,structure:null}};
}
export function restoreLockHandles(paper,id){
 const block=paper?.blocks?.find(part=>part.id===id);
 if(!block)throw new Error('This select no longer exists.');
 const original=paper.refine?.lockedHandles?.[id]||block.boundaryBaseline||block.originalRange;
 if(!original||!Number.isFinite(original.start)||!Number.isFinite(original.end))
  throw new Error('No saved source handles for this select.');
 const source=paper.sources?.[block.sourceId];
 if(!source)throw new Error('Source interview unavailable.');
 const gap=sourceFrameStep(source)||REFINE_FALLBACK_STEP;
 const {max}=sourceBounds(block,source);
 const start=original.start,end=original.end; // Restore exactly what was locked; don't silently resnap.
 if(start<0||end>max+EPS||end-start<gap-EPS)
  throw new Error('Saved handles are outside the source interview.');
 if(Math.abs(start-block.start)<EPS&&Math.abs(end-block.end)<EPS)return paper;
 return {...paper,blocks:paper.blocks.map(part=>part.id===id
  ?{...part,start,end}:part)};
}
export function reviewStatus(paper,block){
 const saved=paper?.refine?.reviews?.[block.id];
 return Boolean(saved?.signature===blockReviewSignature(block));
}
export function markRefined(paper,id,reviewed=true,when=new Date().toISOString()){
 const block=paper?.blocks?.find(b=>b.id===id);
 if(!block)throw new Error('This select is no longer available.');
 const reviews={...(paper.refine?.reviews||{})};
 if(reviewed)reviews[id]={signature:blockReviewSignature(block),reviewedAt:when};
 else delete reviews[id];
 return {...paper,refine:{...paper.refine,reviews}};
}
export function refineSignals(block,source){
 const warnings=[];
 if(!source||!Array.isArray(source.words)||!source.words.length){
  warnings.push({kind:'metadata',severity:'info',label:'Transcript timing unavailable'});
  return warnings;
 }
 const words=source.words.filter(word=>Number.isFinite(word?.start)&&Number.isFinite(word?.end)
  &&word.end>block.start&&word.start<block.end)
  .sort((a,b)=>a.start-b.start);
 if(!words.length){
  warnings.push({kind:'empty',severity:'warning',label:'No transcribed dialogue in these handles'});
  return warnings;
 }
 const first=words[0],last=words.at(-1);
 const pre=Math.max(0,first.start-block.start),post=Math.max(0,block.end-last.end);
 // Boundaries aligned to word timestamps are normal, not 13 separate
 // alarms. Only flag when a word actually crosses the source cut.
 if(first.start<block.start-.04 && first.end>block.start+.04)
  warnings.push({kind:'head',severity:'warning',label:'Possible clipped first word'});
 if(last.end>block.end+.04 && last.start<block.end-.04)
  warnings.push({kind:'tail',severity:'warning',label:'Possible clipped final word'});
 if(pre>.55)warnings.push({kind:'lead',severity:'info',label:`${pre.toFixed(1)}s before the first transcribed word`});
 if(post>.65)warnings.push({kind:'trail',severity:'info',label:`${post.toFixed(1)}s after the last transcribed word`});
 let longest=0;
 for(let i=1;i<words.length;i++)longest=Math.max(longest,words[i].start-words[i-1].end);
 if(longest>1.1)warnings.push({kind:'pause',severity:'info',label:`${longest.toFixed(1)}s pause between transcribed words`});
 return warnings;
}
export function refineSummary(paper){
 const blocks=paper?.blocks||[];
 const clean=blocks.filter(b=>reviewStatus(paper,b)).length;
 const attention=blocks.filter(b=>!reviewStatus(paper,b)&&refineSignals(b,paper.sources?.[b.sourceId])
  .some(s=>s.severity==='warning')).length;
 return {total:blocks.length,reviewed:clean,attention,toReview:blocks.length-clean,
  seconds:blocks.reduce((sum,b)=>sum+Math.max(0,b.end-b.start),0)};
}
export function formatSourceFrames(time,source){
 const fps=sourceFrameStep(source);
 if(!fps)return time.toFixed(2)+'s';
 const rate=Math.round(1/fps),totalFrames=Math.round(time/fps);
 const frames=totalFrames%rate,seconds=Math.floor(totalFrames/rate);
 return [Math.floor(seconds/3600),Math.floor(seconds/60)%60,seconds%60,frames]
  .map(n=>String(n).padStart(2,'0')).join(':');
}
